import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Spinner } from "../../components/Brand";

/**
 * Editable settings for the branded Quotation and Tax Invoice PDF header,
 * numbering prefixes, VAT rate, and legal terms/payment instructions.
 * Backed by GET/PATCH /api/settings/billing.
 */
export default function BillingSettings() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [effective, setEffective] = useState({});
    const [form, setForm] = useState({});
    const [tab, setTab] = useState("company");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/settings/billing");
            setEffective(data.effective || {});
            setForm(data.effective || {});
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            for (const k of ["vat_rate", "quotation_validity_days", "invoice_due_days"]) {
                if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
            }
            const { data } = await api.patch("/settings/billing", payload);
            setEffective(data.effective || {});
            setForm(data.effective || {});
            toast.success("Settings saved — new PDFs will use these values immediately");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <form onSubmit={save} className="space-y-6" data-testid="billing-settings-form">
            <div>
                <div className="lx-label text-[#D4AF37] mb-2">Configuration</div>
                <h1 className="font-serif text-4xl text-white">Billing Settings</h1>
                <p className="text-xs text-white/40 mt-1">
                    Customize the header, terms, VAT and numbering for every Quotation and Tax Invoice PDF.
                </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-[#2B1438]">
                {[
                    ["company", "Company"],
                    ["numbering", "Numbering & VAT"],
                    ["quotation", "Quotation Format"],
                    ["invoice", "Invoice Format"],
                ].map(([k, l]) => (
                    <button
                        key={k}
                        type="button"
                        data-testid={`tab-${k}`}
                        onClick={() => setTab(k)}
                        className={`px-4 py-2 text-xs uppercase tracking-wider transition ${tab === k ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-white/50 hover:text-white"}`}
                    >
                        {l}
                    </button>
                ))}
            </div>

            {tab === "company" && (
                <div className="grid grid-cols-2 gap-5">
                    <Field label="Company Name *">
                        <input required data-testid="cs-name" value={form.company_name || ""} onChange={set("company_name")} className="lx-in-set" />
                    </Field>
                    <Field label="TRN (Tax Registration Number)">
                        <input data-testid="cs-trn" value={form.company_trn || ""} onChange={set("company_trn")} className="lx-in-set" placeholder="100xxxxxxx00003" />
                    </Field>
                    <Field label="Address Line 1">
                        <input data-testid="cs-addr1" value={form.company_address_line1 || ""} onChange={set("company_address_line1")} className="lx-in-set" />
                    </Field>
                    <Field label="Address Line 2">
                        <input value={form.company_address_line2 || ""} onChange={set("company_address_line2")} className="lx-in-set" />
                    </Field>
                    <Field label="City">
                        <input value={form.company_city || ""} onChange={set("company_city")} className="lx-in-set" />
                    </Field>
                    <Field label="Country">
                        <input value={form.company_country || ""} onChange={set("company_country")} className="lx-in-set" />
                    </Field>
                    <Field label="Phone / WhatsApp">
                        <input data-testid="cs-phone" value={form.company_phone || ""} onChange={set("company_phone")} className="lx-in-set" />
                    </Field>
                    <Field label="Email">
                        <input type="email" value={form.company_email || ""} onChange={set("company_email")} className="lx-in-set" />
                    </Field>
                    <Field label="Website">
                        <input value={form.company_website || ""} onChange={set("company_website")} className="lx-in-set" />
                    </Field>
                    <Field label="Logo URL">
                        <input value={form.company_logo_url || ""} onChange={set("company_logo_url")} className="lx-in-set" />
                    </Field>
                </div>
            )}

            {tab === "numbering" && (
                <div className="grid grid-cols-2 gap-5">
                    <Field label="Quotation Prefix" hint="Applies to new quotations only. e.g. QUO → QUO-2026-0001">
                        <input data-testid="cs-quo-prefix" value={form.quotation_prefix || ""} onChange={set("quotation_prefix")} className="lx-in-set" maxLength={10} />
                    </Field>
                    <Field label="Invoice Prefix" hint="Applies to new invoices only. e.g. INV → INV-2026-0001">
                        <input data-testid="cs-inv-prefix" value={form.invoice_prefix || ""} onChange={set("invoice_prefix")} className="lx-in-set" maxLength={10} />
                    </Field>
                    <Field label="VAT Rate (%)">
                        <input data-testid="cs-vat" type="number" min="0" max="100" step="0.1" value={form.vat_rate ?? ""} onChange={set("vat_rate")} className="lx-in-set" />
                    </Field>
                    <Field label="Currency">
                        <input value={form.currency || ""} onChange={set("currency")} className="lx-in-set" placeholder="AED" />
                    </Field>
                    <Field label="Quotation Validity (days)">
                        <input type="number" min="1" max="365" value={form.quotation_validity_days ?? ""} onChange={set("quotation_validity_days")} className="lx-in-set" />
                    </Field>
                    <Field label="Invoice Due (days)">
                        <input type="number" min="1" max="365" value={form.invoice_due_days ?? ""} onChange={set("invoice_due_days")} className="lx-in-set" />
                    </Field>
                </div>
            )}

            {tab === "quotation" && (
                <div className="space-y-5">
                    <Field label="Quotation Terms" hint="Shown at the bottom of every Quotation PDF">
                        <textarea rows={6} data-testid="cs-quo-terms" value={form.quotation_terms || ""} onChange={set("quotation_terms")} className="lx-in-set resize-none" />
                    </Field>
                    <Field label="Default Quotation Notes" hint="Prefilled in the Notes field when creating a new quote (customizable per quote)">
                        <textarea rows={3} value={form.quotation_notes_default || ""} onChange={set("quotation_notes_default")} className="lx-in-set resize-none" />
                    </Field>
                </div>
            )}

            {tab === "invoice" && (
                <div className="space-y-5">
                    <Field label="Payment Instructions" hint="Shown at the bottom of every Tax Invoice PDF">
                        <textarea rows={4} data-testid="cs-pay-instr" value={form.invoice_payment_instructions || ""} onChange={set("invoice_payment_instructions")} className="lx-in-set resize-none" />
                    </Field>
                    <Field label="Invoice Terms">
                        <textarea rows={5} data-testid="cs-inv-terms" value={form.invoice_terms || ""} onChange={set("invoice_terms")} className="lx-in-set resize-none" />
                    </Field>
                    <Field label="Default Invoice Notes" hint="Prefilled in the Notes field when creating a new invoice">
                        <textarea rows={3} value={form.invoice_notes_default || ""} onChange={set("invoice_notes_default")} className="lx-in-set resize-none" />
                    </Field>
                    <Field label="PDF Footer Message">
                        <input value={form.footer_message || ""} onChange={set("footer_message")} className="lx-in-set" />
                    </Field>
                </div>
            )}

            <div className="flex justify-between items-center pt-4 border-t border-[#2B1438]">
                <p className="text-xs text-white/40">
                    Changes take effect immediately for all newly-generated PDFs.
                </p>
                <button
                    type="submit"
                    data-testid="cs-save-btn"
                    disabled={saving}
                    className="lx-btn-gold px-6 py-2.5 text-sm disabled:opacity-60"
                >
                    {saving ? <Spinner /> : "Save Settings"}
                </button>
            </div>

            <style>{`.lx-in-set{width:100%;padding:0.6rem 0.75rem;background:#0F0614;border:1px solid #2B1438;color:white;font-size:.875rem;outline:none}.lx-in-set:focus{border-color:#D4AF37}`}</style>
            {/* Reference effective values so we don't accidentally trip react/no-unused-vars */}
            <input type="hidden" data-testid="cs-effective-name" value={effective.company_name || ""} readOnly />
        </form>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <div className="lx-label text-white/60 mb-1.5">{label}</div>
            {children}
            {hint && <div className="text-[10px] text-white/40 mt-1.5">{hint}</div>}
        </label>
    );
}
